import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  usePayrollByPeriodQuery,
  usePayrollSummaryQuery,
} from "../api/queries";
import { useDepartmentsQuery } from "@/modules/departments/presentation/api/queries";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  useDebounceCallback,
} from "@devhop/ui";
import {
  ArrowLeftIcon,
  HelpCircle,
  ListIcon,
  PlusIcon,
  SearchIcon,
} from "lucide-react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PayrollByPeriodTable } from "../ui/PayrollByPeriodTable";
import { PayrollSummaryCards } from "../ui/PayrollSummaryCards";
import {
  PayrollByPeriodTour,
  usePayrollByPeriodTour,
} from "../tour/PayrollByPeriodTour";

const MONTH_OPTIONS = [
  { value: "1", label: "ມັງກອນ" },
  { value: "2", label: "ກຸມພາ" },
  { value: "3", label: "ມີນາ" },
  { value: "4", label: "ເມສາ" },
  { value: "5", label: "ພຶດສະພາ" },
  { value: "6", label: "ມິຖຸນາ" },
  { value: "7", label: "ກໍລະກົດ" },
  { value: "8", label: "ສິງຫາ" },
  { value: "9", label: "ກັນຍາ" },
  { value: "10", label: "ຕຸລາ" },
  { value: "11", label: "ພະຈິກ" },
  { value: "12", label: "ທັນວາ" },
];

const DEPARTMENT_ALL = "__all__";

const STATUS_ALL = "__all__";
const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: "ທັງໝົດ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "confirmed", label: "ຢືນຢັນ" },
  { value: "paid", label: "ຊຳລະແລ້ວ" },
];

export function PayrollByPeriodPage() {
  const nav = useNavigate({ from: "/app/hr/payroll/period" });
  const search = useSearch({ from: "/app/hr/payroll/period" }) as {
    year?: number;
    month?: number;
    departmentId?: string;
    status?: string;
    search?: string;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const y = Number(search.year ?? new Date().getFullYear());
  const m = Number(search.month ?? new Date().getMonth() + 1);
  const currentYear = new Date().getFullYear();

  const [searchInput, setSearchInput] = useState(search.search ?? "");

  useEffect(() => {
    setSearchInput(search.search ?? "");
  }, [search.search]);

  const periodQuery = usePayrollByPeriodQuery(y, m, {
    limit: 50,
    departmentId:
      search.departmentId && search.departmentId !== DEPARTMENT_ALL
        ? search.departmentId
        : undefined,
    status:
      search.status && search.status !== STATUS_ALL
        ? (search.status as "draft" | "confirmed" | "paid")
        : undefined,
    search: search.search?.trim() || undefined,
    sort: search.sort,
  });
  const summaryQuery = usePayrollSummaryQuery({
    periodYear: y,
    periodMonth: m,
  });
  const departmentsQuery = useDepartmentsQuery({ limit: 200 });

  const data = periodQuery.data?.data ?? [];
  const periodLabel = useMemo(() => {
    const monthOpt = MONTH_OPTIONS.find((o) => o.value === String(m));
    return monthOpt && y ? `${monthOpt.label} ${y}` : `${y}/${m}`;
  }, [m, y]);

  const yearOptions = useMemo(
    () =>
      [currentYear, currentYear - 1, currentYear - 2].map((yr) => ({
        value: String(yr),
        label: String(yr),
      })),
    [currentYear],
  );

  const departmentOptions = useMemo(() => {
    const list = departmentsQuery.data?.data ?? [];
    const base = [{ value: DEPARTMENT_ALL, label: "ທຸກພະແນກ" }];
    const items = (Array.isArray(list) ? list : []).map(
      (d: { id: string; name: string }) => ({
        value: d.id,
        label: d.name,
      }),
    );
    return [...base, ...items];
  }, [departmentsQuery.data]);

  const goToPeriod = useCallback(
    (newYear: number, newMonth: number) => {
      nav({
        to: "/app/hr/payroll/period",
        search: {
          year: newYear,
          month: newMonth,
          departmentId: search.departmentId,
          status: search.status,
          search: search.search,
          sort: search.sort,
        },
      });
    },
    [nav, search.departmentId, search.status, search.search, search.sort],
  );

  const setDepartmentFilter = useCallback(
    (value: string) => {
      nav({
        search: {
          year: y,
          month: m,
          departmentId: value === DEPARTMENT_ALL ? undefined : value,
          status: search.status,
          search: search.search,
          sort: search.sort,
        },
      });
    },
    [nav, y, m, search.status, search.search, search.sort],
  );

  const setStatusFilter = useCallback(
    (value: string) => {
      nav({
        search: {
          year: y,
          month: m,
          departmentId: search.departmentId,
          status: value === STATUS_ALL ? undefined : value,
          search: search.search,
          sort: search.sort,
        },
      });
    },
    [nav, y, m, search.departmentId, search.search, search.sort],
  );

  const debouncedSearch = useDebounceCallback((val: string) => {
    const trimmed = val.trim() || undefined;
    nav({
      search: {
        year: y,
        month: m,
        departmentId: search.departmentId,
        status:
          search.status && search.status !== STATUS_ALL
            ? search.status
            : undefined,
        search: trimmed,
        sort: search.sort,
      },
    });
  }, 400);

  const clearFilters = useCallback(() => {
    setSearchInput("");
    nav({
      search: {
        year: y,
        month: m,
        departmentId: undefined,
        status: undefined,
        search: undefined,
        sort: undefined,
      },
    });
  }, [nav, y, m]);

  const handleSortingChange = useCallback(
    (field: string, desc: boolean) => {
      const dir = desc ? "desc" : "asc";
      const nextSort = [
        { field, dir } as { field: string; dir: "asc" | "desc" },
      ];
      nav({
        search: {
          year: y,
          month: m,
          departmentId: search.departmentId,
          status: search.status,
          search: search.search,
          sort: nextSort,
        },
      });
    },
    [nav, y, m, search.departmentId, search.status, search.search],
  );

  const goToList = useCallback(() => {
    nav({
      to: "/app/hr/payroll",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        filters: undefined,
        periodYear: undefined,
        periodMonth: undefined,
      },
    });
  }, [nav]);

  const goToSlip = useCallback(
    (id: string) => {
      nav({
        to: "/app/hr/payroll/$id/slip",
        params: { id },
        search: { print: undefined },
      });
    },
    [nav],
  );

  const goToDetail = useCallback(
    (id: string) => {
      nav({ to: "/app/hr/payroll/$id/edit", params: { id } });
    },
    [nav],
  );

  const totalCount = periodQuery.data?.meta?.total ?? data.length;
  const tour = usePayrollByPeriodTour();

  return (
    <>
      <Header />
      <Main>
        <PayrollByPeriodTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-2"
          data-tourid="payroll-period-toolbar"
        >
          <div className="flex items-center space-x-2">
            <h1 className="font-semibold text-2xl">
              ເງິນເດືອນຕາມເດືອນ — {periodLabel}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
              title="ເລີ່ມທົດສອບການນຳທາງ"
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={goToList}>
              <ArrowLeftIcon className="h-4 w-4" />
              ລາຍການເງິນເດືອນ
            </Button>
            <Button
              size="sm"
              onClick={() =>
                nav({
                  to: "/app/hr/payroll/period/new",
                  search: { year: y, month: m },
                })
              }
            >
              <PlusIcon className="h-4 w-4" />
              ສ້າງລາຍການເງິນເດືອນ
            </Button>
          </div>
        </div>

        <div data-tourid="payroll-period-summary">
          <PayrollSummaryCards
            data={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
          />
        </div>

        {/* Year–month & filters + table */}
        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="payroll-period-filters"
        >
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[100px]">
              <span className="mb-1.5 block font-medium text-muted-foreground text-xs">
                ປີ
              </span>
              <SimpleSelect
                options={yearOptions}
                value={String(y)}
                onValueChange={(v) => goToPeriod(Number(v) || currentYear, m)}
                placeholder="ປີ"
                className="w-full"
                aria-label="ປີ"
              />
            </div>
            <div className="min-w-[120px]">
              <span className="mb-1.5 block font-medium text-muted-foreground text-xs">
                ເດືອນ
              </span>
              <SimpleSelect
                options={MONTH_OPTIONS}
                value={String(m)}
                onValueChange={(v) => goToPeriod(y, Number(v) || 1)}
                placeholder="ເດືອນ"
                className="w-full"
                aria-label="ເດືອນ"
              />
            </div>
            <div className="min-w-[160px]">
              <span className="mb-1.5 block font-medium text-muted-foreground text-xs">
                ພະແນກ
              </span>
              <SimpleSelect
                options={departmentOptions}
                value={search.departmentId ?? DEPARTMENT_ALL}
                onValueChange={setDepartmentFilter}
                placeholder="ທຸກພະແນກ"
                className="w-full"
                aria-label="ກັ່ນຕອງຕາມພະແນກ"
              />
            </div>
            <div className="min-w-[140px]">
              <span className="mb-1.5 block font-medium text-muted-foreground text-xs">
                ສະຖານະ
              </span>
              <SimpleSelect
                options={STATUS_OPTIONS}
                value={search.status ?? STATUS_ALL}
                onValueChange={setStatusFilter}
                placeholder="ທັງໝົດ"
                className="w-full"
                aria-label="ກັ່ນຕອງຕາມສະຖານະ"
              />
            </div>
            <div className="min-w-[200px] flex-1">
              <span className="mb-1.5 block font-medium text-muted-foreground text-xs">
                ຄົ້ນຫາພະນັກງານ
              </span>
              <div className="relative">
                <SearchIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                  placeholder="ຊື່ ຫຼື ລະຫັດ..."
                  className="h-9 w-full rounded-md border border-input bg-background pl-8 text-sm placeholder:text-muted-foreground"
                  aria-label="ຄົ້ນຫາພະນັກງານ"
                />
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              ລ້າງ
            </Button>
            </div>
          </div>

          <div data-tourid="payroll-period-table">
            <PayrollByPeriodTable
              data={data}
              isLoading={periodQuery.isLoading}
              isError={periodQuery.isError}
              totalCount={totalCount}
              sortBy={search.sort?.[0]?.field}
              sortOrder={search.sort?.[0]?.dir}
              onSortingChange={handleSortingChange}
              onDetail={(row) => goToDetail(row.id)}
              onSlip={(row) => goToSlip(row.id)}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
